import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getAuthHeaders } from "../../api/authHelper";
import "../Admin/VolunteerGroup/VolunteerGroup.css";
import Billede1 from "../../Images/Billede1.jpg";
import PersonSimpleModalForm from "./PersonSimpleModalForm";
import HeroSectionV2 from "../../components/HeroSectionV2";
import { Person } from "../Admin/ID/types";
interface PersonWithShifts extends Person {
    shifts?: {
        shiftId: number;
        title: string;
        functionName: string;
        start: string;
        end: string;
        status: string;
    }[];
}

interface VolunteerGroup {
    id?: number;
    name: string;
    type: string;
    eventId: number;
    maxSlots: number;
    contactPersonId?: number;
    selfAssignShifts: boolean;
    deadline?: string;
}

interface Shift {
    shiftId: number;
    functionId: number;
    functionName: string;
    title: string;
    start: string;
    end: string;
    maxParticipants?: number;
    registeredCount?: number;
    waitlistAllowed?: boolean;
}


const VolunteerGroupsDashboard: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL!;
    const { eventId } = useParams();
    const selectedEventId = eventId ? Number(eventId) : null;
    const [groups, setGroups] = useState<VolunteerGroup[]>([]);
    const [loading, setLoading] = useState(true);

    const [events, setEvents] = useState<any[]>([]);
    const [functions, setFunctions] = useState<any[]>([]);

   
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);

    const [persons, setPersons] = useState<PersonWithShifts[]>([]);
    const [shifts, setShifts] = useState<Shift[]>([]);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedFunctionId, setSelectedFunctionId] = useState<number | "">("");
    const [selectedPerson, setSelectedPerson] = useState<PersonWithShifts | null>(null);

    const [dragOverShiftId, setDragOverShiftId] = useState<number | null>(null);
    const [draggingPersonId, setDraggingPersonId] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const personId = Number(sessionStorage.getItem("personId"));
    const roles: string[] = JSON.parse(sessionStorage.getItem("userRoles") || "[]");
    const isAdmin = roles.includes("Admin");

    const matchesDate = (shift: Shift) => {
        if (!fromDate && !toDate) return true;

        const start = new Date(shift.start);

        if (fromDate && start < new Date(fromDate)) return false;
        if (toDate && start > new Date(toDate + "T23:59:59")) return false;

        return true;
    };

    const canAccessGroup = (group: VolunteerGroup) => {
        if (isAdmin) return true;
        return group.contactPersonId === personId;
    };

    const selectedGroup = selectedGroupId
        ? groups.find(g => g.id === selectedGroupId)
        : undefined;

    const canSelfAssign = selectedGroup?.selfAssignShifts ?? false;

    const loadGroups = async () => {
        const res = await fetch(`${apiUrl}/api/VolunteerGroups`, {
            headers: getAuthHeaders(),
        });

        const data = await res.json();

        const allGroups = data?.$values ?? data ?? [];

        const filteredByEvent = selectedEventId
            ? allGroups.filter((g: VolunteerGroup) => g.eventId === selectedEventId)
            : allGroups;

        setGroups(filteredByEvent);
        setLoading(false);
    };

    useEffect(() => {
        const loadEvents = async () => {
            const res = await fetch(`${apiUrl}/api/events`, {
                headers: getAuthHeaders(),
            });

            const data = await res.json();

            const normalized = (data?.$values ?? data ?? []).map((e: any) => ({
                eventId: e.eventID,
                name: e.eventName
            }));

            setEvents(normalized);
        };

        loadEvents();
    }, []);

    useEffect(() => {
        const loadFunctionsForEvent = async () => {
            if (!selectedEventId) {
                setFunctions([]);
                return;
            }

            const res = await fetch(
                `${apiUrl}/api/events/${selectedEventId}/shifts/with-counts`,
                { headers: getAuthHeaders() }
            );

            if (!res.ok) {
                setFunctions([]);
                return;
            }

            const data = await res.json();

            const uniqueFunctions = Array.from(
                new Map(
                    (data?.$values ?? data ?? []).map((s: any) => [
                        s.functionID,
                        {
                            functionId: s.functionID,
                            functionName: s.functionName,
                            eventId: selectedEventId
                        }
                    ])
                ).values()
            );

            setFunctions(uniqueFunctions);
        };

        loadFunctionsForEvent();
    }, [selectedEventId]);

    useEffect(() => {
        loadGroups();
    }, [selectedEventId]);

    const loadShifts = async (
        eventId: number,
        functionId?: number,
        from?: string,
        to?: string
    ) => {
        let url = `${apiUrl}/api/events/${eventId}/shifts/with-counts`;

        const params = new URLSearchParams();

        if (from) params.append("fromDate", from);
        if (to) params.append("toDate", to);

        if (params.toString()) url += `?${params.toString()}`;

        const res = await fetch(url, {
            headers: getAuthHeaders(),
        });

        if (!res.ok) {
            setShifts([]);
            return;
        }

        const data = await res.json();

        const mapped: Shift[] = (data?.$values ?? data ?? []).map((s: any) => ({
            shiftId: s.shiftsID,
            functionId: s.functionID,
            functionName: s.functionName,
            title: s.shiftsName,
            start: s.startTime,
            end: s.endTime,
            maxParticipants: s.maxParticipants,
            registeredCount: s.registeredCount,
            waitlistAllowed: s.waitlist
        }));

        setShifts(mapped);
    };

    useEffect(() => {
        if (!selectedEventId) return;

        loadShifts(
            selectedEventId,
            selectedFunctionId === "" ? undefined : selectedFunctionId,
            fromDate || undefined,
            toDate || undefined
        );
    }, [selectedEventId, selectedFunctionId, fromDate, toDate]);

    const loadPersonsForGroup = async (groupId: number) => {
        const res = await fetch(
            `${apiUrl}/api/VolunteerGroups/${groupId}/persons`,
            { headers: getAuthHeaders() }
        );

        const data = await res.json();
        console.log("Raw persons from API:", data);
        const personsNormalized: PersonWithShifts[] =
            (data?.$values ?? data ?? []).map((p: any) => {
                console.log("Person before mapping:", {
                    id: p.personId,
                    events: p.events,
                    functions: p.functions,
                });

                return {
                    personId: p.personId,
                    userId: p.userId,
                    firstName: p.firstName,
                    lastName: p.lastName,
                    dateOfBirth: p.dateOfBirth,
                    country: p.country,
                    personImagePath: p.personImagePath,
                    profileUrl: p.profileUrl,
                    hasProfileImage: p.hasProfileImage,

                    events: p.events ?? [],
                    functions: p.functions ?? [],

                    shifts: (p.shifts ?? []).map((s: any) => ({
                        shiftId: s.shiftId,
                        title: s.title,
                        functionName: s.functionName,
                        start: s.start,
                        end: s.end,
                        status: s.status
                    }))
                };
            });

        setPersons(personsNormalized);
    };

    useEffect(() => {
        if (selectedGroupId) loadPersonsForGroup(selectedGroupId);
        else setPersons([]);
    }, [selectedGroupId]);

    const visibleGroups = groups.filter(canAccessGroup);

    const openShifts = shifts
        .filter(matchesDate)
        .filter(s => {
            const matchFunction =
                selectedFunctionId === "" ||
                Number(s.functionId) === Number(selectedFunctionId);

            const max = s.maxParticipants ?? 0;
            const count = s.registeredCount ?? 0;

            const isOpen = max === 0 || count < max;
            const canWaitlist = s.waitlistAllowed === true;

            return matchFunction && (isOpen || canWaitlist);
        });

    if (loading) return <div>Indlæser frivilliggrupper...</div>;

    if (selectedGroup) {
        return (
            <div>
                <HeroSectionV2
                    titleEn="Volunteer Groups"
                    titleDa="Frivillig Grupper"
                    descriptionEn="..."
                    descriptionDa="Her kan du administrere personer"
                    imageSrc={Billede1}
                />

                <div style={{ padding: 20 }}>
                    <button className="edit-btn" onClick={() => setSelectedGroupId(null)}>
                        ← Tilbage
                    </button>

                    <h1>{selectedGroup.name}</h1>

                    {/* FILTER BOX (separate, full width) */}
                    <div className="shift-filters shift-filters-card">
                        <h2>
                            Event: {events.find(e => e.eventId === selectedEventId)?.name ?? ""}
                        </h2>

                        <input
                            type="date"
                            disabled={!canSelfAssign}
                            value={fromDate}
                            onChange={e => setFromDate(e.target.value)}
                        />

                        <input
                            type="date"
                            disabled={!canSelfAssign}
                            value={toDate}
                            onChange={e => setToDate(e.target.value)}
                        />

                        <select
                            value={selectedFunctionId}
                            disabled={!canSelfAssign}
                            onChange={(e) =>
                                setSelectedFunctionId(e.target.value ? Number(e.target.value) : "")
                            }
                        >
                            <option value="">Alle funktioner</option>

                            {functions.map((f: any) => (
                                <option key={f.functionId} value={f.functionId}>
                                    {f.functionName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="group-dashboard-layout">

                        {/* LEFT */}
                        <div className="persons-section">
                            <div className="section-header-card">
                                <h3>Personer i gruppen</h3>
                            </div>

                            <table className="persons-table">
                                <thead>
                                    <tr>
                                        <th>Fornavn</th>
                                        <th>Efternavn</th>
                                        <th>Billede</th>
                                        <th>Vagter</th>
                                        <th></th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {persons.map(p => (
                                        <tr
                                            key={p.personId}
                                            draggable
                                            className={draggingPersonId === p.personId ? "dragging" : ""}
                                            onDragStart={(e) => {
                                                setDraggingPersonId(p.personId);
                                                e.dataTransfer.setData("personId", String(p.personId));
                                            }}
                                            onDragEnd={() => setDraggingPersonId(null)}
                                        >
                                            <td>{p.firstName}</td>

                                            <td>
                                                <div>{p.lastName}</div>
                                            </td>
                                            <td>
                                                <span
                                                    style={{
                                                        padding: "2px 8px",
                                                        borderRadius: 8,
                                                        fontSize: 12,
                                                        background: p.hasProfileImage ? "#d1fae5" : "#fee2e2",
                                                        color: p.hasProfileImage ? "#065f46" : "#991b1b",
                                                    }}
                                                >
                                                    {p.hasProfileImage ? "Image" : "No image"}
                                                </span>
                                            </td>
                                                {/* NEW: SHIFTS COLUMN */}
                                            <td>
                                                <div className="shift-list">
                                                    {(() => {
                                                        const assignedShifts = (p.shifts ?? []).filter(
                                                            (s) => (s.status ?? "").trim().toLowerCase() === "tilmeldt"
                                                        );

                                                        if (!assignedShifts.length) {
                                                            return <span className="shift-empty">Ingen vagter</span>;
                                                        }

                                                        return assignedShifts.map((s) => (
                                                            <div key={s.shiftId} className="shift-chip">
                                                                <div className="shift-title">
                                                                    {s.functionName} – {s.title}
                                                                </div>

                                                                <div className="shift-time">
                                                                    {new Date(s.start).toLocaleDateString()}{" "}
                                                                    {new Date(s.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                                    {" → "}
                                                                    {new Date(s.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                                </div>
                                                            </div>
                                                        ));
                                                    })()}
                                                </div>
                                            </td>

                                            <td>
                                                <button
                                                    className="edit-btn"
                                                    onClick={() => {
                                                        setSelectedPerson(p);
                                                        setShowModal(true);
                                                    }}
                                                >
                                                    Rediger
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* RIGHT */}
                        <div className="shifts-section">

                            

                            {/* HEADER (ONLY TITLE NOW) */}
                            <div className="section-header-card">
                                <h3>Vagter</h3>
                            </div>

                            <table className="persons-table">
                                <thead>
                                    <tr>
                                        <th>Funktion</th>
                                        <th>Vagt</th>
                                        <th>Start</th>
                                        <th>Slut</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {!canSelfAssign && (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                style={{
                                                    textAlign: "center",
                                                    fontWeight: "bold",
                                                    color: "#dc2626",
                                                    textTransform: "uppercase",
                                                    fontSize: "18px",
                                                    padding: "12px",
                                                }}
                                            >
                                                SHIFT ASSIGNMENT DISABLED FOR THIS GROUP
                                            </td>
                                        </tr>
                                    )}

                                    {openShifts.map(s => (
                                        <tr
                                            key={s.shiftId}
                                            className={dragOverShiftId === s.shiftId ? "drop-hover" : ""}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                setDragOverShiftId(s.shiftId);
                                            }}
                                            onDragLeave={() => setDragOverShiftId(null)}
                                            onDrop={async (e) => {
                                                e.preventDefault();
                                                setDragOverShiftId(null);

                                                const personId = Number(e.dataTransfer.getData("personId"));
                                                if (!personId) return;

                                                await fetch(`${apiUrl}/api/shifts/${s.shiftId}/register`, {
                                                    method: "POST",
                                                    headers: {
                                                        ...getAuthHeaders(),
                                                        "Content-Type": "application/json"
                                                    },
                                                    body: JSON.stringify({
                                                        personId,
                                                        status: "Tilmeldt"
                                                    })
                                                });

                                                if (selectedGroupId) {
                                                    loadPersonsForGroup(selectedGroupId);
                                                }
                                            }}
                                        >
                                            <td>{s.functionName}</td>
                                            <td>{s.title}</td>
                                            <td>{new Date(s.start).toLocaleString()}</td>
                                            <td>{new Date(s.end).toLocaleString()}</td>
                                            <td>
                                                {(() => {
                                                    const max = s.maxParticipants ?? 0;
                                                    const count = s.registeredCount ?? 0;

                                                    if (max === 0) return "Åben";
                                                    if (count < max) return "Ledig";
                                                    return s.waitlistAllowed ? "Venteliste" : "Lukket";
                                                })()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                        </div>
                    </div>
                </div>
                {/* MODAL */}
                {showModal && selectedPerson && (
                    <PersonSimpleModalForm
                        initialPerson={selectedPerson}
                        currentUserId={personId}
                        onCancel={() => {
                            setShowModal(false);
                            setSelectedPerson(null);
                        }}
                        onSave={async (p) => {
                            setShowModal(false);
                            setSelectedPerson(null);

                            const payload = {
                                personId: p.personId,
                                firstName: p.firstName,
                                lastName: p.lastName,
                                dateOfBirth: p.dateOfBirth,
                                country: p.country,
                                personImagePath: p.personImagePath,
                                uploadImageBase64: p.uploadImageBase64,

                                // optional but safe if backend ignores it
                                events: p.events ?? selectedPerson?.events ?? [],
                                functions: p.functions ?? selectedPerson?.functions ?? [],
                            };

                            await fetch(`${apiUrl}/api/VolunteerGroups/person/${p.personId}`, {
                                method: "PUT",
                                headers: {
                                    ...getAuthHeaders(),
                                    "Content-Type": "application/json",
                                },
                                body: JSON.stringify(payload),
                            });

                            if (selectedGroupId) {
                                loadPersonsForGroup(selectedGroupId);
                            }
                        }}
                    />
                )}
            </div>
        );
    }


    return (
        <div>
            <HeroSectionV2
                titleEn="Volunteer Groups"
                titleDa="Frivillig Grupper"
                descriptionEn="..."
                descriptionDa="Grupper"
                imageSrc={Billede1}
            />

            <div style={{ padding: 20 }}>
                <h1>Frivilliggrupper Dashboard</h1>

                <table className="persons-table">
                    <thead>
                        <tr>
                            <th>Navn</th>
                            <th>Type</th>
                            <th></th>
                        </tr>
                    </thead>

                    <tbody>
                        {visibleGroups.map(g => (
                            <tr key={g.id}>
                                <td>{g.name}</td>
                                <td>{g.type}</td>
                                <td>
                                    <button onClick={() => setSelectedGroupId(g.id!)}>
                                        Åbn
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default VolunteerGroupsDashboard;